import { faPenToSquare } from "@fortawesome/free-solid-svg-icons/faPenToSquare";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { TagChip } from "../../../components/TagChip";
import { Text } from "../../../components/Text";
import { WarningModal } from "../../../components/WarningModal";
import { Tag } from "../../../model/tag/Tag";
import { GetTagUsageResponseDto } from "../../../model/tag/dto/GetTagUsageResponseDto";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { tagSliceSelectors } from "../../../redux/tag/tag.selector";
import { tagActions } from "../../../redux/tag/tag.slice";
import { Rounded } from "../../../theme/Rounded";
import { Spacing } from "../../../theme/Spacing";
import { TestIDs } from "../../../tests/types/testIds";
import { AtiraToast } from "../../../utils/AtiraToast";
import { Tooltip } from "../../../utils/local-antd";
import { SettingsTagCreateEditModal } from "../components/SettingsTagCreateEditModal";
import { SettingsWrapper } from "../components/SettingsWrapper";
import { Info } from "../../../components/Info";

const TagRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.s};
  padding: ${Spacing.s} ${Spacing.m};
  border-radius: ${Rounded.md};
  border: 1px solid ${({ theme }) => theme.sub};
`;

const IconActionButton = styled(Button)<{ isDanger?: boolean }>`
  background: transparent;
  padding: ${Spacing.s};
  margin: 0;
  color: ${({ theme }) => theme.darkerSub};

  &:hover {
    background: ${({ theme }) => theme.sub};
    color: ${({ theme, isDanger }) => theme[isDanger ? "danger" : "main"]};
  }
`;

const CreateButton = styled(Button)`
  margin: 0;
  width: fit-content;
`;

export const SettingsTags: React.FC = () => {
  const [createEditOpen, setCreateEditOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);
  const [tagToDelete, setTagToDelete] = useState<Tag | null>(null);
  const [tagToDeleteUsage, setTagToDeleteUsage] =
    useState<GetTagUsageResponseDto | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const tags = useAppSelector(tagSliceSelectors.selectTags);

  const onCreate = () => {
    setSelectedTag(null);
    setCreateEditOpen(true);
  };

  const onEdit = (tag: Tag) => {
    setSelectedTag(tag);
    setCreateEditOpen(true);
  };

  const onAskDelete = async (tag: Tag) => {
    setTagToDelete(tag);
    setTagToDeleteUsage(null);

    try {
      const usage = await dispatch(
        tagActions.getTagUsage({ tagId: tag._id }),
      ).unwrap();
      setTagToDeleteUsage(usage);
    } catch (e) {
      console.log(e);
    }
  };

  const getDeleteDescription = () => {
    const { entries = 0, conversations = 0 } = tagToDeleteUsage || {};

    const parts = [
      entries
        ? t("settings.tags.delete.description.entries", { count: entries })
        : "",
      conversations
        ? t("settings.tags.delete.description.chats", {
            count: conversations,
          })
        : "",
    ].filter(Boolean);

    if (!parts.length) {
      return t("settings.tags.delete.description");
    }

    return t("settings.tags.delete.description.used", {
      usage:
        parts.length === 2
          ? t("settings.tags.delete.description.and", {
              first: parts[0],
              second: parts[1],
            })
          : parts[0],
    });
  };

  const onDelete = async () => {
    if (!tagToDelete) {
      return;
    }

    try {
      setDeleteLoading(true);
      await dispatch(tagActions.deleteTag({ tagId: tagToDelete._id })).unwrap();
      AtiraToast.success(t("settings.tags.delete.success"));
      setTagToDelete(null);
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setDeleteLoading(false);
    }
  };

  useEffect(() => {
    dispatch(tagActions.getTags());
  }, [dispatch]);

  return (
    <SettingsWrapper>
      <Text fontWeight={"bold"}>{t("settings.tags.title")}</Text>
      <Info info={t("settings.tags.description")} />

      <Flex flexDirection="column" gap="s" marginTop="s">
        {tags.length ? (
          tags.map((tag) => (
            <TagRow
              key={tag._id}
              dataTestId={TestIDs.general.settings.tags.row(tag.name)}
            >
              <TagChip tag={tag} />

              <Flex gap="xs">
                <Tooltip title={t("common.edit")}>
                  <IconActionButton
                    icon={faPenToSquare}
                    onClick={() => onEdit(tag)}
                    dataTestId={TestIDs.general.settings.tags.edit(tag.name)}
                  />
                </Tooltip>

                <Tooltip title={t("common.delete")}>
                  <IconActionButton
                    icon={faTrash}
                    onClick={() => onAskDelete(tag)}
                    isDanger
                    dataTestId={TestIDs.general.settings.tags.delete(tag.name)}
                  />
                </Tooltip>
              </Flex>
            </TagRow>
          ))
        ) : (
          <Text fontSize="s" color="darkerSub" align="center">
            {t("settings.tags.empty")}
          </Text>
        )}
      </Flex>

      <CreateButton
        onClick={onCreate}
        icon={faPlus}
        dataTestId={TestIDs.general.settings.tags.create}
      >
        {t("settings.tags.create.title")}
      </CreateButton>

      <SettingsTagCreateEditModal
        open={createEditOpen}
        onClose={() => setCreateEditOpen(false)}
        tag={selectedTag}
      />

      <WarningModal
        title={t("settings.tags.delete.title", { name: tagToDelete?.name })}
        description={getDeleteDescription()}
        onConfirm={onDelete}
        isOpen={Boolean(tagToDelete)}
        onClose={() => setTagToDelete(null)}
        loading={deleteLoading}
        confirmButtonDataTestId={TestIDs.general.settings.tags.deleteConfirm}
      />
    </SettingsWrapper>
  );
};
